'use client';

import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { MetaDefault } from '../../../components/typography';
import { Select } from '../../../components/select';
import { SettingsRow } from '../../settings/SettingsRow';
import { Switch } from '../../../components/switch';
import { TechnologyIcon } from '../../technology-icon/TechnologyIcon';
import { Tooltip } from '../../../components/tooltip';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowUpDownIcon,
  RefreshIcon,
  SecurityCheckIcon,
  Tick01Icon,
  Unlink01Icon,
} from '@terra/ui-icons';
import { formatCloudProviderLabel } from '@terra/shared-contracts-integrations';
import type { DnsZoneSurfaceAttachmentDto } from '@terra/shared-contracts-external-network';
import { CatalogRowIconAction } from './CatalogRowIconAction';
import {
  DELEGATION_COLOR,
  DNS_ZONE_AUTO_SCOPE_OPTIONS,
  STATUS_COLOR,
  canAttachDnsZone,
  resolveAutoScope,
  shouldShowDnsZoneRecheck,
  type DnsZoneAutoScope,
  type DnsZoneAutoScopeCollection,
} from './dns-zone-attach.utils';

type DnsZoneCatalogRowProps = {
  attachment: DnsZoneSurfaceAttachmentDto;
  isReadOnly: boolean;
  isBusy: boolean;
  isSyncing: boolean;
  isAttaching: boolean;
  isDetaching: boolean;
  isVerifying: boolean;
  lastSyncedText: string;
  neverSyncedLabel: string;
  scopeCollection: DnsZoneAutoScopeCollection;
  onAttach: (attachment: DnsZoneSurfaceAttachmentDto) => void;
  onDetach: (attachment: DnsZoneSurfaceAttachmentDto) => void;
  onSync: (attachment: DnsZoneSurfaceAttachmentDto) => void;
  onVerify: (attachment: DnsZoneSurfaceAttachmentDto) => void;
  onEnabledChange: (input: {
    zoneId: string;
    autoScope: DnsZoneAutoScope;
    enabled: boolean;
  }) => void;
  onAutoScopeChange: (input: {
    zoneId: string;
    autoScope: DnsZoneAutoScope;
  }) => void;
  labels: {
    enabled: string;
    scopePlaceholder: string;
    attach: string;
    detach: string;
    detachAria: string;
    runSync: string;
    recheck: string;
    privateBadge: string;
    status: (
      status: NonNullable<DnsZoneSurfaceAttachmentDto['status']>,
    ) => string;
    delegation: (
      status: NonNullable<DnsZoneSurfaceAttachmentDto['delegationStatus']>,
    ) => string;
    recordCount: (count: number) => string;
  };
};

export function DnsZoneCatalogRow({
  attachment,
  isReadOnly,
  isBusy,
  isSyncing,
  isAttaching,
  isDetaching,
  isVerifying,
  lastSyncedText,
  neverSyncedLabel,
  scopeCollection,
  onAttach,
  onDetach,
  onSync,
  onVerify,
  onEnabledChange,
  onAutoScopeChange,
  labels,
}: DnsZoneCatalogRowProps) {
  const zoneId = attachment.zoneId;
  const autoScope = resolveAutoScope(attachment.autoScope);
  const canMutate = !isReadOnly && !isBusy && Boolean(zoneId);
  const title =
    attachment.zoneName ?? attachment.zoneRef ?? attachment.provider;
  const providerLabel = formatCloudProviderLabel({
    provider: attachment.provider,
  });
  const subtitleParts = [
    providerLabel,
    attachment.accountRef,
    attachment.recordCount != null
      ? labels.recordCount(attachment.recordCount)
      : null,
  ].filter((part): part is string => Boolean(part));
  const statusDescription =
    attachment.status === 'SYNC_ERROR' && attachment.lastError
      ? attachment.lastError
      : attachment.lastSyncedAt
        ? lastSyncedText
        : neverSyncedLabel;
  const showDelegationBadge =
    attachment.delegationStatus != null &&
    attachment.delegationStatus !== 'VERIFIED';
  const observed =
    attachment.observedNameservers.length > 0
      ? attachment.observedNameservers.join(', ')
      : null;

  return (
    <SettingsRow
      leadingIcon={
        <TechnologyIcon name={attachment.provider} size={16} showInitials />
      }
      label={title}
      description={
        <VStack alignItems="flex-start" gap={0.5}>
          {subtitleParts.length > 0 ? (
            <MetaDefault color="fg.neutral.subtle">
              {subtitleParts.join(' · ')}
            </MetaDefault>
          ) : null}
          <HStack gap={2} alignItems="center" flexWrap="wrap">
            {attachment.isPrivate ? (
              <Badge variant="outline" size="sm" colorPalette="gray">
                {labels.privateBadge}
              </Badge>
            ) : null}
            {showDelegationBadge && attachment.delegationStatus ? (
              <Tooltip
                title={
                  observed ?? labels.delegation(attachment.delegationStatus)
                }
                positioning={{ strategy: 'fixed' }}
              >
                <Badge
                  variant="outline"
                  size="sm"
                  colorPalette={DELEGATION_COLOR[attachment.delegationStatus]}
                >
                  {labels.delegation(attachment.delegationStatus)}
                </Badge>
              </Tooltip>
            ) : null}
            {attachment.status && attachment.status !== 'ACTIVE' ? (
              <Badge
                variant="outline"
                size="sm"
                colorPalette={STATUS_COLOR[attachment.status]}
              >
                {labels.status(attachment.status)}
              </Badge>
            ) : null}
            <MetaDefault color="fg.neutral.subtle">
              {statusDescription}
            </MetaDefault>
          </HStack>
        </VStack>
      }
      control={
        <HStack gap={2} alignItems="center">
          {attachment.attached && zoneId ? (
            <>
              <Box minW="9.5rem">
                <Select.Root
                  collection={scopeCollection}
                  value={[autoScope]}
                  disabled={!canMutate || !attachment.enabled}
                  onValueChange={({ value }) => {
                    const next = DNS_ZONE_AUTO_SCOPE_OPTIONS.find(
                      (option) => option === value[0],
                    );

                    if (!next || next === autoScope) {
                      return;
                    }

                    onAutoScopeChange({ zoneId, autoScope: next });
                  }}
                  positioning={{ sameWidth: true, strategy: 'fixed' }}
                  size="sm"
                  width="full"
                >
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder={labels.scopePlaceholder} />
                      <ArrowUpDownIcon size={14} />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {scopeCollection.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator>
                            <Tick01Icon size={14} />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Box>
              {shouldShowDnsZoneRecheck(attachment) ? (
                <CatalogRowIconAction
                  label={labels.recheck}
                  disabled={!canMutate}
                  loading={isVerifying}
                  onClick={() => onVerify(attachment)}
                >
                  <SecurityCheckIcon size={16} />
                </CatalogRowIconAction>
              ) : null}
              <CatalogRowIconAction
                label={labels.runSync}
                disabled={!canMutate}
                loading={isSyncing}
                onClick={() => onSync(attachment)}
              >
                <RefreshIcon size={16} />
              </CatalogRowIconAction>
              <CatalogRowIconAction
                label={labels.detach}
                ariaLabel={labels.detachAria}
                disabled={!canMutate}
                loading={isDetaching}
                onClick={() => onDetach(attachment)}
              >
                <Unlink01Icon size={16} />
              </CatalogRowIconAction>
              <Tooltip
                title={labels.enabled}
                positioning={{ strategy: 'fixed' }}
              >
                <Switch
                  size="sm"
                  checked={attachment.enabled ?? false}
                  disabled={!canMutate}
                  aria-label={labels.enabled}
                  onCheckedChange={({ checked }) => {
                    if (checked === (attachment.enabled ?? false)) {
                      return;
                    }

                    onEnabledChange({
                      zoneId,
                      autoScope,
                      enabled: checked,
                    });
                  }}
                />
              </Tooltip>
            </>
          ) : (
            <>
              {shouldShowDnsZoneRecheck(attachment) ? (
                <CatalogRowIconAction
                  label={labels.recheck}
                  disabled={!canMutate}
                  loading={isVerifying}
                  onClick={() => onVerify(attachment)}
                >
                  <SecurityCheckIcon size={16} />
                </CatalogRowIconAction>
              ) : null}
              <CatalogRowIconAction
                label={labels.runSync}
                disabled={isReadOnly || isBusy}
                loading={isSyncing}
                onClick={() => onSync(attachment)}
              >
                <RefreshIcon size={16} />
              </CatalogRowIconAction>
              {canAttachDnsZone(attachment) ? (
                <Button
                  size="xs"
                  disabled={isReadOnly || isBusy}
                  loading={isAttaching}
                  onClick={() => onAttach(attachment)}
                >
                  {labels.attach}
                </Button>
              ) : null}
            </>
          )}
        </HStack>
      }
    />
  );
}
